import type { ProductSkuValue } from "@sveabank/cc-common";
import { ProductSku, sum } from "@sveabank/cc-common";
import type { Images } from "../../images";
import type { OnboardingPackage, OnboardingPackageId } from "../../types/package";
import type {
	CreditAndMonthlyFees,
	MonthlyFeesObject,
	ProductDefinitionId,
	SelectedCreditProduct,
	SelectedProduct,
} from "../../types/product";
import type { CreditScoreAssessment, CreditScoreStepState } from "../../types/step";

export const getTotalPackageCost = (onboardingPackage: OnboardingPackage) => {
	const packageCost = onboardingPackage.baseMonthlyFee.amount;
	const totalProductCost = onboardingPackage.products.reduce(
		(acc, curr) =>
			acc +
			Number(
				curr.type === "CheckCredit"
					? curr.config.monthlyFees[0]?.fee.amount
					: curr.config.monthlyFee.amount,
			),
		0,
	);

	return Number(packageCost) + totalProductCost;
};

export const getAddonTotalPrice = (addons: SelectedProduct[]) =>
	addons.reduce((acc, o) => acc + Number(o.monthlyFee.amount), 0);

// Todo Replace creditscore with selected packages endpoint
export const calculateCredit = (
	creditScore: CreditScoreStepState,
	formValues: Record<string, number | string>,
	creditProducts: SelectedCreditProduct[],
) => {
	const availableCredit = Number(creditScore?.assessment?.limit.amount ?? 0);
	const consumedCredit = Number(creditScore?.initialConsumedCredit ?? 0);

	// Combine config credit products with local form values
	const creditProductTotal = creditProducts.reduce((acc: number[], curr) => {
		// Replace credit config values with form values if there is overlap (editing)
		const value = formValues[curr.productId];

		return [...acc, Number(value) || Number(curr.chosenCredit.amount)];
	}, []);

	const totalConsumedCredit = availableCredit - consumedCredit - sum(creditProductTotal);
	return totalConsumedCredit;
};

export const calculateCreditPercentage = (
	remainingCredit: number,
	creditScore: CreditScoreStepState,
) => {
	const availableCredit = Number(creditScore?.assessment?.limit.amount ?? 0);
	const consumedCreditInPercentage = (remainingCredit / availableCredit) * 100;

	return Math.min(Math.max(consumedCreditInPercentage, 0), 100);
};
// TODO update to selected package endpoint
export const getCreditScoreMinValue = (
	product: SelectedCreditProduct,
	creditScore: CreditScoreStepState,
) => {
	switch (product.productDefinitionId) {
		case "CREDITCARD":
			return Number(creditScore?.assessment?.card.minLimit.amount ?? 0);
		case "CHECKKREDIT":
			return Number(creditScore?.assessment?.check.minLimit.amount ?? 0);
		default:
			return 0;
	}
};

export const getCreditScoreMaxValue = (
	product: SelectedCreditProduct,
	creditScore: CreditScoreStepState,
) => {
	switch (product.productDefinitionId) {
		case "CREDITCARD":
			return Number(creditScore?.assessment?.card.maxLimit.amount ?? 0);
		case "CHECKKREDIT":
			return Number(creditScore?.assessment?.check.maxLimit.amount ?? 0);
		default:
			return 0;
	}
};
export const setupInitialCreditConfig = (
	selectedProducts: SelectedCreditProduct[],
	creditScore: CreditScoreStepState,
) =>
	selectedProducts.reduce<Record<string, string>>((acc, curr) => {
		acc[curr.productId] = (
			Number(curr.chosenCredit.amount) || getCreditScoreMinValue(curr, creditScore)
		).toString();
		return acc;
	}, {});

const getCheckCreditCost = (amount: number, brackets: MonthlyFeesObject[]): MonthlyFeesObject => {
	const minBracket = brackets[0];
	if (minBracket && amount < +minBracket.limit.amount) {
		return minBracket;
	}

	const foundBracket = brackets
		.map((bracket) => ({
			...bracket,
			limit: { ...bracket.limit, amount: bracket.limit.amount },
		}))
		.find((bracket) => amount <= +bracket.limit.amount);

	if (!foundBracket) {
		return {
			fee: { amount: "0", currency: "sek" },
			limit: { amount: "0", currency: "sek" },
		};
	}
	return foundBracket;
};

export type CostSummaryItem = [
	OnboardingPackageId | ProductDefinitionId,
	ProductSkuValue | undefined,
	number,
];

export const isCreditWorthy = (assessment: CreditScoreAssessment) =>
	assessment.assessmentResult !== "rejected" && Number(assessment.limit.amount) >= 10000;

export const getCreditProductCostsSummary = (
	checkCreditProductBrackets: () => CreditAndMonthlyFees | null,
	selectedPackageId: OnboardingPackageId,
	selectedPackageFee: string,
	selectedCreditProducts: SelectedCreditProduct[],
	addons: SelectedCreditProduct[],
	values: Record<string, string>,
): CostSummaryItem[] => {
	const costSummaryArray: CostSummaryItem[] = [[selectedPackageId, undefined, +selectedPackageFee]];

	const checkCreditProduct = selectedCreditProducts.find(
		(product) => product.productDefinitionId === "CHECKKREDIT",
	);

	if (checkCreditProduct) {
		const chosenCreditValue = values[checkCreditProduct.productId];
		const brackets = checkCreditProductBrackets();

		if (!chosenCreditValue) throw new Error("No chosen credit value");

		if (brackets) {
			costSummaryArray.push([
				checkCreditProduct.productDefinitionId,
				checkCreditProduct.sku,
				+getCheckCreditCost(+chosenCreditValue, brackets.monthlyFees).fee.amount,
			]);
		}
	}

	const creditCardProducts = addons.filter(
		(product) => product.productDefinitionId === "CREDITCARD",
	);
	if (creditCardProducts.length > 0) {
		const creditCardProduct = creditCardProducts[0];
		if (!creditCardProduct) throw new Error("Should have creditCardProduct but it was not found");

		costSummaryArray.push([
			creditCardProduct.productDefinitionId,
			creditCardProduct.sku,
			creditCardProducts.reduce((acc, curr) => acc + +curr.monthlyFee.amount, 0),
		]);
	}

	return costSummaryArray;
};

export const productImages: Partial<Record<ProductSkuValue, keyof typeof Images>> = {
	[ProductSku.CreditCard]: "CREDITCARD",
	[ProductSku.DebitCard]: "DEBITCARD",
	[ProductSku.ForetagarnaCreditCard]: "FORETAGARNA_CREDIT",
	[ProductSku.ForetagarnaDebitCard]: "FORETAGARNA_DEBIT",
};
